import React from 'react'
import ToastContext from './ToastContext'
import type { ToastProps } from './Toast'
import ToastContainer from './ToastContainer'

interface ToastProviderProps {
  children: React.ReactNode
}

export default function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = React.useState<Map<string, ToastProps>>(new Map())

  const getToasts = React.useCallback(() => {
    return toasts
  }, [toasts])

  const getToast = React.useCallback((key: string) => {
    // istanbul ignore next
    return toasts.get(key)
  }, [toasts])

  const addToast = React.useCallback((toast: ToastProps) => {
    setToasts((prevToasts) => {
      const newToasts = new Map(prevToasts)
      const toastKey = toast.toastKey || Math.random().toString(36).substr(2, 9)
      
      // Merge with existing toast if present
      const existingToast = newToasts.get(toastKey)
      newToasts.set(toastKey, {
        ...existingToast, 
        ...toast,        
        toastKey,        
        placement: toast.placement ?? existingToast?.placement ?? 'top' 
      })
      
      return newToasts
    })
  }, [])

  const removeToast = React.useCallback((key: string) => {
    setToasts((prevToasts) => {
      const newToasts = new Map(prevToasts)
      newToasts.delete(key)
      return newToasts
    })
  }, [])

  const toastsArray = React.useMemo(() => Array.from(toasts.values()), [toasts])

  return (
    <ToastContext.Provider value={{ getToasts, getToast, addToast, removeToast }}>
      {children}
      <ToastContainer toasts={toastsArray} removeToast={removeToast} />
    </ToastContext.Provider>
  )
}
